import React, { useEffect, useState } from "react";
import { Flame, CaretDown, CaretUp } from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function statusBadge(n) {
  if (n.type_category === "RX") return { label: "RX", cls: "bg-purple-500/15 text-purple-300 border-purple-500/40" };
  if (!n.is_active) return { label: "Inactive", cls: "bg-tac-surface text-tac-faint border-tac-border" };
  if (n.percent_contained != null && n.percent_contained >= 100) {
    return { label: "Out", cls: "bg-tac-surface text-tac-dim border-tac-border" };
  }
  if (n.percent_contained != null && n.percent_contained > 0) {
    return { label: `${Math.round(n.percent_contained)}%`, cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" };
  }
  return { label: "Active", cls: "bg-tac-primary/15 text-tac-primary border-tac-primary/50" };
}

export default function NifcIncidentsCard({ incidents = [], onFlyTo, onHover, hoverId = null, hoverPool = [] }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!hoverId) return;
    if (!incidents.some((n) => n.id === hoverId) && !hoverPool.some((n) => n.id === hoverId)) return;
    setOpen(true);
    const t = setTimeout(() => {
      document
        .querySelector(`[data-testid="nifc-row-${hoverId}"]`)
        ?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 150);
    return () => clearTimeout(t);
  }, [hoverId, incidents, hoverPool]);

  if (!incidents || incidents.length === 0) return null;

  const sorted = [...incidents].sort((a, b) => {
    // Active first, then by size desc, then by discovered date desc
    if ((b.is_active ? 1 : 0) !== (a.is_active ? 1 : 0)) {
      return (b.is_active ? 1 : 0) - (a.is_active ? 1 : 0);
    }
    const sa = a.incident_size ?? 0;
    const sb = b.incident_size ?? 0;
    if (sb !== sa) return sb - sa;
    return (b.discovered_at || "").localeCompare(a.discovered_at || "");
  });

  return (
    <section
      data-testid="nifc-incidents-card"
      className="border-b border-tac-border bg-tac-surface/40"
    >
      <button
        type="button"
        data-testid="nifc-card-toggle"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-2.5 text-left hover:bg-tac-surface/60 transition-colors"
      >
        <div className="flex items-center gap-2">
          <Flame size={14} weight="fill" className="text-tac-primary" />
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-text">
            NIFC Active Fires
          </span>
          <span className="text-[15px] font-mono-tac tabular text-tac-faint">
            · {incidents.length}
          </span>
        </div>
        {open ? (
          <CaretUp size={12} weight="bold" className="text-tac-dim" />
        ) : (
          <CaretDown size={12} weight="bold" className="text-tac-dim" />
        )}
      </button>

      {open && (
        <ul className="divide-y divide-tac-border/50" data-testid="nifc-incidents-list">
          {(() => {
            const visible = sorted.slice(0, 8);
            if (hoverId && !visible.some((n) => n.id === hoverId)) {
              const extra = sorted.find((n) => n.id === hoverId) || hoverPool.find((n) => n.id === hoverId);
              if (extra) visible.push(extra);
            }
            return visible;
          })().map((n) => {
            const sb = statusBadge(n);
            const rowHovered = n.id === hoverId;
            return (
              <li
                key={n.id}
                data-testid={`nifc-row-${n.id}`}
                className={`px-4 py-2 border-l-2 transition-colors ${
                  rowHovered ? "bg-tac-surface2 border-l-tac-primary" : "border-l-transparent"
                }`}
              >
                <button
                  type="button"
                  onClick={() => n.latitude != null && onFlyTo?.(n.latitude, n.longitude)}
                  onMouseEnter={() => onHover?.(n.id)}
                  onMouseLeave={() => onHover?.(null)}
                  className="w-full text-left group"
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <div className="font-display font-bold text-xs text-tac-text leading-tight group-hover:text-tac-primary transition-colors truncate">
                      {n.name}
                    </div>
                    <span className={`shrink-0 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${sb.cls}`}>
                      {sb.label}
                    </span>
                  </div>
                  <div className="text-[15px] font-mono-tac tabular text-tac-dim flex items-center gap-2 flex-wrap">
                    {n.county && <span>{n.county} Co.</span>}
                    {n.incident_size != null && (
                      <span>{Math.round(n.incident_size).toLocaleString()} ac</span>
                    )}
                    {n.modified_at && <span className="text-tac-faint">· {fmtDate(n.modified_at)}</span>}
                  </div>
                </button>
              </li>
            );
          })}
          {sorted.length > 8 && (
            <li className="px-4 py-1.5 text-[15px] font-mono-tac text-tac-faint text-center">
              + {sorted.length - 8} more · zoom out to see all
            </li>
          )}
        </ul>
      )}
      <div className="px-4 py-1.5 text-[10px] font-mono-tac text-tac-faint border-t border-tac-border/40">
        Source: NIFC WFIGS Current Wildland Fire Incident Locations
      </div>
    </section>
  );
}
